import { useState } from "react";

import SteppedProgress, { type SteppedProgressStep } from "@imminently/decisively-ui/SteppedProgress";

import { ComponentPage, ComponentPreview, ComponentSection } from "../components/ComponentPage";

const exampleSteps: SteppedProgressStep[] = [
  { key: "details", label: "Details", state: "success" },
  { key: "connection", label: "Connection", state: "success" },
  { key: "mapping", label: "Mapping", state: "in-progress" },
  { key: "warning", label: "Manual input", state: "warning" },
  { key: "error", label: "Failed request", state: "destructive" },
  { key: "review", label: "Review", state: "not-started" },
  { key: "finish", label: "Finish", state: "not-started" },
];

const longProgress: SteppedProgressStep[] = Array.from({ length: 12 }, (_, index) => ({
  key: `step-${index + 1}`,
  label: `Step ${index + 1}`,
  state: index < 4 ? "success" : index === 4 ? "in-progress" : "not-started",
}));

export function SteppedProgressPage() {
  const [currentIndex, setCurrentIndex] = useState(2);

  return (
    <ComponentPage
      title="Stepped Progress"
      description="Shows progress across a known sequence of steps. It supports clickable navigation, a current-step indicator, lifecycle states, and a visual-only mode."
    >
      <ComponentSection
        title="Interactive"
        description="Select a segment to move the current-step indicator. Every segment exposes its label and state to assistive technology."
      >
        <ComponentPreview className="flex-col items-stretch gap-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">
              Step {currentIndex + 1} of {exampleSteps.length}: {exampleSteps[currentIndex]?.label}
            </span>
            <span className="text-muted-foreground">{exampleSteps[currentIndex]?.state}</span>
          </div>
          <SteppedProgress
            ariaLabel="Example setup steps"
            currentIndex={currentIndex}
            onSelect={setCurrentIndex}
            steps={exampleSteps}
          />
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="States"
        description="Complete, started, warning, error, and incomplete states remain distinct without relying on colour alone."
      >
        <ComponentPreview className="items-stretch">
          <SteppedProgress
            ariaLabel="Progress state examples"
            currentIndex={1}
            onSelect={() => {}}
            steps={exampleSteps}
          />
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Visual Only"
        description="Use visual-only mode when another control already provides the accessible step navigation."
      >
        <ComponentPreview className="flex-col items-stretch gap-3">
          <span className="text-sm font-medium">4 of 12 complete</span>
          <SteppedProgress
            currentIndex={4}
            interactive={false}
            steps={longProgress}
          />
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
